import { QueueLead } from '@/api/callcenter/dashboard';
import { parseApiOrIsoDate } from '@/features/callcenter/logic';
import { formatMoney } from '@/utils/format';
import { format } from 'date-fns';

type QueueCardDetailsProps = {
  lead: QueueLead;
};

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="min-w-0">
    <span className="font-medium text-foreground/70">{label}:</span>{' '}
    <span className="text-muted-foreground">{value}</span>
  </div>
);

const hoursFrom = (minutes: number | null): string =>
  minutes && minutes > 0 ? `${minutes / 60} hrs` : '?';

// The queue payload carries raw Carbon values, which encode as ISO-8601 with
// a trailing Z rather than the API's usual naive "Y-m-d H:i:s". formatApiDate
// only understands the naive shape and throws "Invalid time value" on the
// other, so timestamps here go through parseApiOrIsoDate -- the tolerant
// reader the rest of this feature already uses -- and never throw: an
// unparseable value renders as nothing rather than taking the card down.
const formatMoment = (value: string | null, pattern: string): string | null => {
  const parsed = parseApiOrIsoDate(value);

  return parsed ? format(parsed, pattern) : null;
};

// A date-only value carries no instant. Reading it as UTC midnight and then
// rendering in local time moves it a day backwards, so it is formatted from
// its own parts. Datetime values still go through the parser above.
const formatDay = (value: string | null): string | null => {
  if (!value) return null;

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (dateOnly) {
    const [, year, month, day] = dateOnly;

    return `${month}/${day}/${year}`;
  }

  return formatMoment(value, 'MM/dd/yyyy');
};

/**
 * The same detail the leads listing shows, so an agent can size a lead up
 * without opening it — and deliberately nothing else. The listing pairs this
 * with log/status/follow-up/archive shortcuts; those stay off the queue,
 * because working a lead has to happen on the lead screen where it is
 * recorded against the assignment.
 *
 * Read-only by construction: this component renders no interactive elements
 * at all, which is also what lets QueueCard place it next to a button without
 * nesting controls inside one.
 */
export const QueueCardDetails = ({ lead }: QueueCardDetailsProps) => {
  const location = [lead.city, lead.state].filter(Boolean).join(', ');
  const activities = lead.activity_interest?.filter(Boolean) ?? [];

  return (
    <div data-testid="queue-card-details" className="space-y-1 border-t pt-2 text-xs">
      {lead.organization_name && (
        <div className="font-medium text-foreground">{lead.organization_name}</div>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
        <Fact label="Date wanted" value={formatDay(lead.date_wanted) ?? 'Not set'} />
        <Fact label="Length" value={hoursFrom(lead.length_wanted)} />
        {lead.event_type_name && <Fact label="Event" value={lead.event_type_name} />}
        <Fact label="Quote" value={lead.total ? formatMoney(lead.total) : 'Not quoted'} />
        {formatDay(lead.followup_at) && (
          <Fact label="Follow-up" value={formatDay(lead.followup_at)} />
        )}
        {(location || lead.zipcode) && (
          <Fact label="Location" value={[location, lead.zipcode].filter(Boolean).join(' ')} />
        )}
      </div>

      {activities.length > 0 && <Fact label="Interested in" value={activities.join(', ')} />}
      {lead.email && <Fact label="Email" value={lead.email} />}

      {lead.special_instructions && (
        <Fact label="Special instructions" value={lead.special_instructions} />
      )}

      {/* Only meaningful while the customer is waiting on a reply — the API
          returns it under exactly that condition. */}
      {lead.latest_message && <Fact label="Latest message" value={lead.latest_message} />}

      {lead.latest_note && (
        <Fact
          label="Latest note"
          value={
            <>
              {formatMoment(lead.latest_note_at, 'MM/dd/yyyy h:mm a') && (
                <span className="italic">
                  ({formatMoment(lead.latest_note_at, 'MM/dd/yyyy h:mm a')}){' '}
                </span>
              )}
              {lead.latest_note}
            </>
          }
        />
      )}

      {formatMoment(lead.updated_at, 'MM/dd/yyyy h:mm a') && (
        <Fact label="Last update" value={formatMoment(lead.updated_at, 'MM/dd/yyyy h:mm a')} />
      )}
    </div>
  );
};
